import { useEffect, useRef, useState } from "react";
import { createDoorScene, webglAvailable } from "@/lib/doorScene";

// Full-hero WebGL layer; the door is framed inside `region` via a camera view offset so the title can travel across the hero.
export const DoorHero3D = ({ intro, region, onMode }) => {
  const stageRef = useRef(null);
  const mountRef = useRef(null);
  const labelsRef = useRef(null);
  const leadersRef = useRef(null);
  const [mode, setMode] = useState("loading");
  const [reduced] = useState(() => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false);

  useEffect(() => { onMode?.(mode); }, [mode, onMode]);

  useEffect(() => {
    if (!webglAvailable()) { setMode("fallback"); intro?.onFallback(); return undefined; }
    let scene;
    try {
      scene = createDoorScene({
        stage: stageRef.current, mount: mountRef.current, labels: labelsRef.current, leaders: leadersRef.current,
        reduced, intro, region: region?.current,
      });
    } catch (e) {
      setMode("fallback");
      intro?.onFallback();
      return undefined;
    }
    setMode("webgl");
    const io = new IntersectionObserver(([entry]) => scene.setActive(entry.isIntersecting), { threshold: 0 });
    io.observe(stageRef.current);
    return () => { io.disconnect(); scene.dispose(); };
  }, [reduced, intro, region]);

  return (
    <div
      ref={stageRef}
      className="cd-stage absolute inset-x-0 bottom-0 top-16 overflow-hidden"
      data-testid="hero-3d-stage"
      data-mode={mode}
      data-motion={reduced ? "reduced" : "auto"}
      aria-label="Animated 3D door breaking into its seven hardware parts"
      role="img"
    >
      {mode !== "fallback" && (
        <>
          <div ref={mountRef} className="cd-stage-canvas absolute inset-0" />
          <svg ref={leadersRef} className="cd-leaders pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true" />
          <div ref={labelsRef} className="pointer-events-none absolute inset-0" aria-hidden="true" />
        </>
      )}
    </div>
  );
};
